// The op-chain ⇄ ViewConfig bridge. The pipeline builder edits an ordered Op[];
// this folds it (or a prefix) into the canonical ViewConfig the shared engine and
// viewer consume, so a pipeline-built report renders identically to a flat one.
// Also re-exports the pure shape-probing the builder palette needs, derived from
// the compiled prefix — there is no separate pipeline engine.
import { FRAMES, frameOf } from './frames'
import { AggFn, FieldSchema, FrameId, Op, PickableOp } from './pipeline-types'
import { Filter, Metric, ViewConfig } from './view-config'

/** Whether `field` is a date column of `frame` (drives groupby bucketing). */
export const isDate = (field: string, frame: FrameId): boolean =>
  FRAMES[frame].dateFields.includes(field)

/**
 * Resolve an agg+field pair to the frame's *named* measure (so a compiled
 * report's column header/legend reads "Yearly Revenue", not "sum(annual_revenue)").
 * Falls back to a generated label for pairs the frame doesn't name.
 */
export function measureFor(
  frame: FrameId,
  agg: AggFn,
  field: string | null
): Metric {
  // A running total is a sum that the engine later accumulates (via the
  // `cumulative` flag opsToConfig sets), so it resolves to the same named sum
  // measure — the label/legend reads "Yearly Revenue", the values run cumulative.
  if (agg === 'cumsum') return measureFor(frame, 'sum', field)
  // A running count is a count the engine later accumulates (cumulative flag) —
  // same named count measure, values run cumulative.
  if (agg === 'cumcount') return measureFor(frame, 'count', null)
  const measures = FRAMES[frame].measures
  if (agg === 'distinct') {
    // Unique count of a field — no frame names it, so synthesize a label like
    // "Distinct Owner". Reads as a measure column header / legend.
    const label = FRAMES[frame].labels[field ?? ''] ?? field ?? ''
    return {
      agg: 'distinct',
      field: field ?? undefined,
      label: `Distinct ${label}`,
    }
  }
  if (agg === 'count') {
    return (
      measures.find((m) => m.agg === 'count') ?? {
        agg: 'count',
        label: 'Count',
      }
    )
  }
  const named = measures.find((m) => m.agg === agg && m.field === field)
  if (named) return named
  const label = FRAMES[frame].labels[field ?? ''] ?? field ?? ''
  return {
    agg,
    field: field ?? undefined,
    label: agg === 'avg' ? `Avg ${label}` : label,
  }
}

/** Map a pipeline filter op (already in ViewConfig vocab) to a ViewConfig Filter. */
function opFilterToFilter(op: Extract<Op, { type: 'filter' }>): Filter {
  switch (op.op) {
    case 'in':
      return { field: op.field, op: 'in', value: op.value as string[] }
    case 'gte':
      return { field: op.field, op: 'gte', value: Number(op.value) }
    case 'lte':
      return { field: op.field, op: 'lte', value: Number(op.value) }
    case 'within_days':
      return { field: op.field, op: 'within_days', value: Number(op.value) }
    case 'date_after':
      return { field: op.field, op: 'date_after', value: String(op.value) }
    case 'date_before':
      return { field: op.field, op: 'date_before', value: String(op.value) }
    case 'neq':
      return { field: op.field, op: 'neq', value: String(op.value) }
    case 'eq':
    default:
      return { field: op.field, op: 'eq', value: String(op.value) }
  }
}

/**
 * Fold ops[0..upTo) into a ViewConfig. `kind` falls out of the chain: an
 * agg/groupby present → aggregate; otherwise list. The default `chart` follows
 * the shape (bar for categorical, line for a time bucket, table otherwise); the
 * ViewSwitcher repaints from there.
 */
export function opsToConfig(
  ops: Op[],
  frame: FrameId,
  upTo?: number
): ViewConfig {
  const lim = upTo == null ? ops.length : upTo
  let columns: string[] | undefined
  let groupBy: ViewConfig['groupBy']
  let seriesBy: string | undefined
  let metrics: Metric[] | undefined
  let sort: ViewConfig['sort']
  let limit: number | undefined
  let cumulative = false
  const filters: Filter[] = []

  for (let i = 0; i < lim; i++) {
    const op = ops[i]
    if (op.type === 'filter') filters.push(opFilterToFilter(op))
    else if (op.type === 'select') columns = op.cols
    else if (op.type === 'agg') {
      groupBy = undefined
      // A bare aggregate collapses to one scalar — there's no chronological axis
      // to accumulate along, so a running total can't apply here.
      cumulative = false
      metrics = [measureFor(frame, op.aggFn, op.measure)]
    } else if (op.type === 'groupby') {
      metrics = [measureFor(frame, op.aggFn, op.measure || null)]
      if (op.dim === '__none__') groupBy = undefined
      else if (isDate(op.dim, frame))
        groupBy = {
          field: op.dim,
          bucket: (op.bucket as 'month' | 'quarter') || 'quarter',
        }
      else groupBy = { field: op.dim }
      // Running total/count only fires over a time bucket; a cum* agg on a
      // categorical or collapsed group-by silently degrades to its underlying
      // sum/count (the cumulative flag just stays off).
      cumulative =
        (op.aggFn === 'cumsum' || op.aggFn === 'cumcount') && !!groupBy?.bucket
    } else if (op.type === 'pivot') {
      // A pivot is a self-contained 2D aggregation: it sets both axes and the
      // measure at once (rows = groupBy, columns = seriesBy), reading straight
      // from the base rows rather than splitting an already-collapsed group-by.
      metrics = [measureFor(frame, op.aggFn, op.measure || null)]
      groupBy = isDate(op.rowDim, frame)
        ? {
            field: op.rowDim,
            bucket: (op.bucket as 'month' | 'quarter') || 'quarter',
          }
        : { field: op.rowDim }
      seriesBy = op.colDim
      // Over a time row axis a running total/count accumulates along the ordered
      // buckets — the engine does this per column series. Off a date axis (or a
      // bare row dim) there's nothing to accumulate, so the flag stays off.
      cumulative =
        (op.aggFn === 'cumsum' || op.aggFn === 'cumcount') && !!groupBy?.bucket
    } else if (op.type === 'sort') sort = { by: op.field, dir: op.dir }
    else if (op.type === 'limit') limit = op.n
  }
  // A series split only means anything atop a group-by; if the chain has no (or
  // a collapsed) group-by, drop the dangling split.
  if (!groupBy) seriesBy = undefined

  const base = {
    id: 'pipeline-preview',
    name: '',
    description: '',
    icon: 'chart' as const,
    frame,
    filters,
    knobs: [],
  }

  if (metrics) {
    return {
      ...base,
      kind: 'aggregate',
      groupBy,
      seriesBy,
      metrics,
      chartMetric: metrics[0].label,
      cumulative: cumulative || undefined,
      chart: groupBy ? (groupBy.bucket ? 'line' : 'bar') : 'table-only',
      limit,
    }
  }
  return { ...base, kind: 'list', columns, sort, limit, chart: 'table-only' }
}

/** Columns + inferred types of the frame as a new op at `upTo` would see it. */
export function schemaAt(
  ops: Op[],
  frame: FrameId,
  upTo?: number
): FieldSchema[] {
  const cfg = opsToConfig(ops, frame, upTo)
  const fr = frameOf(frame)
  if (cfg.kind === 'aggregate') {
    if (!cfg.groupBy) return [] // terminal metric — single scalar
    const type: FieldSchema['type'] = isDate(cfg.groupBy.field, frame)
      ? 'date'
      : 'cat'
    const dim: FieldSchema = { name: cfg.groupBy.field, type }
    if (type === 'cat') {
      dim.values = [
        ...new Set(
          fr.records.map((r) => r[cfg.groupBy!.field]).filter((x) => x != null)
        ),
      ] as FieldSchema['values']
    }
    return [
      dim,
      ...(cfg.metrics ?? []).map((m) => ({
        name: m.label,
        type: 'num' as const,
      })),
    ]
  }
  if (cfg.columns) {
    return cfg.columns.map(
      (name) =>
        fr.fields.find((f) => f.name === name) ?? {
          name,
          type: 'cat' as const,
        }
    )
  }
  return fr.fields
}

/** Which ops are valid given the frame as it stands at `upTo`. */
export function validOps(
  ops: Op[],
  frame: FrameId,
  upTo?: number
): PickableOp[] {
  const cfg = opsToConfig(ops, frame, upTo)
  if (cfg.kind === 'aggregate' && !cfg.groupBy) return [] // terminal scalar
  // Once a group-by (or pivot) has collapsed the rows, the detail is gone — you
  // can only filter (HAVING), sort, or limit the result. Re-shaping ops
  // (group-by / pivot / aggregate / column select) operate on rows that no
  // longer exist, so they drop out of the palette. This is what makes pivot
  // honest: it's an alternative to group-by, not a magic split layered on top.
  if (cfg.kind === 'aggregate' && cfg.groupBy)
    return ['filter', 'sort', 'limit']
  // Pre-aggregation (raw / filtered rows): the full shaping palette. Pivot is a
  // 2D aggregate, so it needs a categorical column to split on plus a second
  // dim (categorical or date) for the row axis.
  const { catDims, dateFields } = frameOf(frame)
  const canPivot =
    catDims.length >= 1 && catDims.length + dateFields.length >= 2
  return [
    'filter',
    'select',
    'sort',
    'groupby',
    ...(canPivot ? (['pivot'] as PickableOp[]) : []),
    'aggregate',
    'limit',
  ]
}

/** Measure label for a chip, prefixing the running (cum*) variants. */
function aggChipLabel(
  frame: FrameId,
  aggFn: AggFn,
  measure: string | null
): string {
  const base = measureFor(frame, aggFn, measure).label
  if (aggFn === 'cumsum') return `Running total of ${base}`
  if (aggFn === 'cumcount') return `Running ${base}`
  return base
}

/** Short monospace label for a pipeline step chip. */
export function chipText(op: Op, frame: FrameId): string {
  const label = (f: string): string => FRAMES[frame].labels[f] ?? f
  if (op.type === 'filter') {
    const v = Array.isArray(op.value) ? `[${op.value.join(', ')}]` : op.value
    return `filter: ${label(op.field)} ${op.op} ${v}`
  }
  if (op.type === 'groupby') {
    const meas = aggChipLabel(frame, op.aggFn, op.measure || null)
    if (op.dim === '__none__') return `aggregate: ${meas} (collapse all)`
    if (isDate(op.dim, frame))
      return `groupby: ${label(op.dim)}/${op.bucket || 'quarter'} → ${meas}`
    return `groupby: ${label(op.dim)} → ${meas}`
  }
  if (op.type === 'pivot') {
    const meas = aggChipLabel(frame, op.aggFn, op.measure || null)
    const rows = isDate(op.rowDim, frame)
      ? `${label(op.rowDim)}/${op.bucket || 'quarter'}`
      : label(op.rowDim)
    return `pivot: ${rows} × ${label(op.colDim)} → ${meas}`
  }
  if (op.type === 'select') return `select: ${op.cols.map(label).join(', ')}`
  if (op.type === 'agg')
    return `aggregate: ${measureFor(frame, op.aggFn, op.measure).label}`
  if (op.type === 'sort') return `sort: ${label(op.field)} ${op.dir}`
  if (op.type === 'limit') return `limit: ${op.n}`
  return (op as Op).type
}
